import { useDroppable } from '@dnd-kit/core';
import { ComponentType, memo, MutableRefObject, PropsWithChildren } from 'react';
import { NodeType, type TimelineNode } from '../../types';
import { isGanttZoneDroppable } from '../../utils/isGanttZoneDroppable';
import { StyledDropArea } from './StyledDropArea';
import { getExtraDropzoneStatus, isGanttNodeDraggable } from '../../utils';

export type NewDroppableAreaData<T = TimelineNode> = {
  isQueueDisabled: boolean;
  node: T;
  nextNode?: TimelineNode;
  previousNode?: TimelineNode;
  queueId: number;
};

export type WrappedDroppableAreaProps = {
  readonly droppable: ReturnType<typeof useDroppable>;
  readonly dropzoneRef: MutableRefObject<HTMLDivElement | null>;
};

export const withDroppableArea = <T,>(Component: ComponentType<T>) => {
  const WithDroppableArea = memo(
    ({
      isQueueDisabled,
      nextNode,
      previousNode,
      children,
      queueId,
      ...props
    }: PropsWithChildren<T & NewDroppableAreaData>) => {
      const predecessorTaskId = getPredecessorTaskId({ node: props.node, previousNode });
      const beforeDropzone = hasBeforeDropzone(props.node, previousNode);

      const afterDroppable = useDroppable({
        id: `${props.node.id}-after-droppable`,
        data: {
          queueId,
          predecessorTaskId,
        },
        disabled: nextNode?.type === NodeType.EMPTY || isQueueDisabled || isGanttZoneDroppable(props.node),
      });

      const beforeDroppable = useDroppable({
        id: `${props.node.id}-before-droppable`,
        data: {
          queueId,
        },
        disabled: !beforeDropzone,
      });

      if (props.node.type === NodeType.EMPTY) {
        return (
          <Component {...(props as T)}>
            {children}
            <div
              className="relative h-11 shrink-0"
              ref={afterDroppable.setNodeRef}
              style={{
                width: props.node.nodeWidth,
                maxWidth: props.node.nodeWidth,
              }}
            >
              <StyledDropArea
                className="absolute left-0"
                isOver={afterDroppable.isOver}
                width={Math.min(getDraggedNodeWidth(afterDroppable), props.node.nodeWidth)}
              />
            </div>
          </Component>
        );
      }

      const afterDroppableRef = nextNode?.type === NodeType.EMPTY ? null : afterDroppable.setNodeRef;
      const isNotDraggingSelf = beforeDroppable.active?.data?.current?.id === props.node.id;

      return (
        <>
          {beforeDropzone && !isNotDraggingSelf && (
            <StyledDropArea
              withAnimation
              isOver={beforeDroppable.isOver}
              width={getDraggedNodeWidth(beforeDroppable)}
            />
          )}
          <Component {...(props as T)}>
            <>
              {children}
              {/* Trigger for before dropzone */}
              {beforeDropzone && <div className="absolute -left-2.5 h-11 w-2" ref={beforeDroppable.setNodeRef} />}

              {/* Trigger for end dropzone */}
              {!!afterDroppableRef && <div className="absolute -right-2.5 h-11 w-2" ref={afterDroppableRef} />}
            </>
          </Component>
          {!!afterDroppableRef && !isNotDraggingSelf && (
            <StyledDropArea withAnimation isOver={afterDroppable.isOver} width={getDraggedNodeWidth(afterDroppable)} />
          )}
        </>
      );
    },
  );

  WithDroppableArea.displayName = 'withDroppableArea';

  return WithDroppableArea;
};

const getDraggedNodeWidth = (droppable: ReturnType<typeof useDroppable>) => {
  const activeDraggedWidth = droppable.active && droppable.active.rect.current.initial?.width;

  return activeDraggedWidth ? activeDraggedWidth : 0;
};

const hasBeforeDropzone = (node: TimelineNode, previousNode?: TimelineNode) => {
  const droppable =
    node.type === NodeType.MULTIPLE ? node?.overlappingNodes?.every(isGanttNodeDraggable) : isGanttNodeDraggable(node);

  return getExtraDropzoneStatus(previousNode) && droppable;
};

type GetPredecessorTaskIdProps = {
  previousNode?: TimelineNode;
  node?: TimelineNode;
};

const getPredecessorTaskId = ({ node, previousNode }: GetPredecessorTaskIdProps): number | undefined => {
  if (node?.type === NodeType.EMPTY || node?.type === NodeType.BREAK) {
    return getPredecessorTaskId({ node: previousNode });
  }

  if (node?.type === NodeType.MULTIPLE) {
    const notBreak = node.overlappingNodes.filter(({ type }) => type !== NodeType.BREAK);

    const lastNode = notBreak[notBreak.length - 1];

    return getPredecessorTaskId({ node: lastNode });
  }

  if (node?.type === NodeType.BATCH) {
    const lastNode = node.tasks?.[node.tasks?.length - 1];

    return lastNode?.id;
  }

  if (node?.type === NodeType.TASK) {
    return node.id;
  }
};
